import { useQueries, UseQueryOptions } from '@tanstack/react-query';
import queryClient from '@/feature/query/queryClient';
import { ONE_HOUR, TEN_MINUTES } from '@/util/time';
import { fetchPrices } from './api';

export const priceKey = (articleId: number, deliveryPointId: string) => [
  'price',
  articleId,
  deliveryPointId,
];

export const pricesKey = (articleIds: number[], deliveryPointId: string) => [
  'prices',
  articleIds,
  deliveryPointId,
];

const queryOptions = (articleId: number, deliveryPointId: string) => {
  return {
    queryKey: pricesKey([articleId], deliveryPointId),
    // Prevents prices from being fetched too often
    staleTime: TEN_MINUTES,
    gcTime: ONE_HOUR,
  };
};

interface UsePricesQueryOptions {
  enabled?: boolean;
  onPriceSuccess?: (price: PriceAndAvailability, articleId: number) => void;
}

type PriceQueryInfo = {
  articleId: number;
  deliveryPointId?: string;
};

/**
 * Queries prices for a list of article id and delivery point id combinations.
 *
 * @param info List of article id and delivery point id combinations.
 * @param options Query options
 */
export function usePricesQuery(
  info: PriceQueryInfo[],
  options: UsePricesQueryOptions = {}
) {
  const { enabled, onPriceSuccess = () => {} } = options;

  const queries: UseQueryOptions[] = info
    .filter(({ deliveryPointId }) => deliveryPointId !== undefined)
    .filter(uniqueArticlesAndDeliveryPoints)
    .map(({ articleId, deliveryPointId }) => ({
      ...queryOptions(articleId, deliveryPointId!),
      queryFn: async (): Promise<PriceAndAvailability[]> => {
        const prices = await fetchPrices([articleId], deliveryPointId!);
        const [price] = prices;
        onPriceSuccess(price, articleId);
        queryClient.setQueryData(
          priceKey(price.articleId, deliveryPointId!),
          price
        );
        return prices;
      },
      enabled,
    }));

  return useQueries({ queries });
}

function uniqueArticlesAndDeliveryPoints(
  info: PriceQueryInfo,
  index: number,
  array: PriceQueryInfo[]
) {
  return (
    array.findIndex(
      (entry) =>
        entry.articleId === info.articleId &&
        entry.deliveryPointId === info.deliveryPointId
    ) === index
  );
}
